import React, { useState, useEffect, useRef } from "react";
import { useNotificationStore } from "../../store/notificationStore";
import { useCrewStore } from "../../store/crewStore";
import { escHtml } from "../../utils";

const WINDOW_MS = 10 * 60 * 1000;

const NotificationBell: React.FC = () => {
  const { list, markAllRead, clearAll, initialLoadDone } =
    useNotificationStore();
  const crews = useCrewStore((s) => s.crews);
  const checkIncoming = useNotificationStore((s) => s.checkIncoming);

  const [open, setOpen] = useState(false);
  const [, tick] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const unread = list.filter((n) => !n.readAt).length;

  // Check incoming whenever crews change
  useEffect(() => {
    if (initialLoadDone) checkIncoming(crews);
  }, [crews, initialLoadDone]);

  // Live timer tick when drawer is open
  useEffect(() => {
    if (open) {
      timerRef.current = setInterval(() => tick((t) => t + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) markAllRead();
    setOpen((o) => !o);
  };

  const formatTimer = (createdAt: string) => {
    const age = Date.now() - new Date(createdAt).getTime();
    if (age >= WINDOW_MS)
      return { label: "Locked", urgent: false, locked: true };
    const rem = WINDOW_MS - age;
    const mins = Math.floor(rem / 60000);
    const secs = Math.floor((rem % 60000) / 1000);
    return {
      label: `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`,
      urgent: rem < 60000,
      locked: false,
    };
  };

  return (
    <div
      id="notif-bell"
      style={{
        position: "fixed",
        top: 12,
        right: 90,
        zIndex: 998,
        cursor: "pointer",
      }}
    >
      <div className="nb-btn" onClick={handleOpen}>
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M13.73 21a2 2 0 0 1-3.46 0"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        {unread > 0 && (
          <div className="nb-badge">{unread > 9 ? "9+" : unread}</div>
        )}
      </div>

      {open && (
        <div id="notif-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="nd-header">
            <div className="nd-title">Notifications</div>
            <button
              className="nd-clear-btn"
              onClick={() => {
                clearAll();
                setOpen(false);
              }}
            >
              Clear all
            </button>
          </div>
          {list.length === 0 ? (
            <div className="nd-empty">No notifications yet</div>
          ) : (
            list.map((n) => {
              const timer =
                n.type === "confirmed" ? formatTimer(n.createdAt) : null;
              return (
                <div key={n.id} className="nd-item">
                  <div className={`nd-dot ${n.type}`} />
                  <div className="nd-body">
                    <div className="nd-msg">
                      {n.type === "confirmed" ? "✓" : "⚠"} {escHtml(n.name)}{" "}
                      {n.type === "confirmed" ? "confirmed" : "declined"} route
                    </div>
                    <div className="nd-sub">
                      {escHtml(n.role)} · {escHtml(n.workDate)}
                      {n.note ? ` · "${escHtml(n.note)}"` : ""}
                    </div>
                    <div className="nd-sub" style={{ fontSize: 10, color: "#454850", marginTop: 2 }}>
                      {(() => {
                        try {
                          return new Date(n.createdAt).toLocaleString("en-GB", {
                            timeZone: "Europe/London",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          });
                        } catch { return ""; }
                      })()}
                    </div>
                  </div>
                  {timer && (
                    <div
                      className={`nd-timer ${timer.locked ? "locked" : timer.urgent ? "urgent" : ""}`}
                    >
                      {timer.label}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
